// Light / dark: match the device, or pick one. Kept per device (localStorage); index.html applies it before the
// app loads so there's no flash. CSS: dark is the default; light comes from the device setting unless
// <html data-theme="dark">, or from data-theme="light".
import { useSyncExternalStore } from 'react';
import { store } from './device';

export type ThemePref = 'system' | 'light' | 'dark';

const KEY = 'tempo.theme';
const COLORS = { dark: '#0b0d12', light: '#f6f7fb' };
const listeners = new Set<() => void>();
const media = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: light)') : null;

export const getTheme = (): ThemePref => {
  const t = store.get<string>(KEY, 'system');
  return t === 'light' || t === 'dark' ? t : 'system';
};

/** What's showing now, after the device setting is taken into account. */
export const effectiveTheme = (pref = getTheme()): 'light' | 'dark' => (pref !== 'system' ? pref : media?.matches ? 'light' : 'dark');

export function applyTheme(pref = getTheme()) {
  const root = document.documentElement;
  if (pref === 'system') delete root.dataset.theme;
  else root.dataset.theme = pref;
  // The status bar / browser chrome colour follows too.
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    const own = meta.media.includes('light') ? COLORS.light : COLORS.dark;
    meta.content = pref === 'system' ? own : COLORS[pref];
  }
}

export function setTheme(pref: ThemePref) {
  store.set(KEY, pref);
  applyTheme(pref);
  for (const l of listeners) l();
}

media?.addEventListener('change', () => {
  if (getTheme() === 'system') for (const l of listeners) l();
});

export function useTheme(): ThemePref {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    getTheme,
  );
}
